/**
 * Facial balancing / liquid facelift pricing — the ONE source for every dollar figure
 * on /lp/facial-balancing-dallas and /lp/liquid-facelift-dallas (hotspot cards, the
 * "Build your plan" configurator, the pricing table and the FAQ all read from here).
 */

// PRICING: APPROVED by owner 2026-10-08 (Dr. Kohli, in chat): the plan rule, the flat
// additional-syringe price (every HA area, under-eye and nose included) and the gift (see below).
// Radiesse MAP to verify before publish: Merz runs a unilateral minimum-advertised-price
// policy (HCP portal). Check it before this page prints the biostimulator price live.
export const FACIAL_BALANCING_PRICING = {
  /** Hyaluronic-acid filler, à la carte, per syringe. */
  perSyringe: {
    lip: 675,
    cheek: 850,
    chin: 850,
    jawline: 850,
    temple: 850,
    underEye: 950,
    /** Non-surgical nose (liquid rhinoplasty), menu price from lakewoodmedspa.com. */
    nose: 950,
  },
  /** Collagen biostimulator (Sculptra or Radiesse), per vial/syringe. Never part of the HA plan rule. */
  biostimulatorPerVial: 799,
  /**
   * PRF under-eye (platelet-rich fibrin from the patient's own blood, by cannula). Catalog
   * "PRF Under-Eye with Cannula" $699 (verified 2026-10-11). Not hyaluronic acid: never part of the
   * HA plan rule and never counted toward the gift. Owner 10/11: offered for under-eye shadows,
   * hollows and dark circles alongside tear-trough filler.
   */
  prfUnderEye: 699,
  /**
   * Multi-syringe HA plan (APPROVED by owner 2026-10-08). Swap `rule` (see PLAN_RULES) to change how plans are priced.
   * "first-then-flat": the first syringe is charged at its own area price, every additional
   * syringe at `additionalSyringe`, and the plan total is never more than à la carte.
   * `additionalSyringe` applies to every HA area, including under-eye and nose (APPROVED by owner 2026-10-08).
   */
  plan: {
    rule: "first-then-flat",
    additionalSyringe: 700,
  },
  /**
   * Gift-with-plan: Pico Red Carpet laser glow peel, $399 value, with 3+ HA syringes
   * (APPROVED by owner 2026-10-08). Unlocks when the plan has at least
   * `minHaSyringes` hyaluronic-acid syringes; biostimulator vials do not count. No percentages.
   */
  gift: {
    name: "Pico Red Carpet laser glow peel",
    value: 399,
    minHaSyringes: 3,
    timing: "about 2 weeks after your filler visit, so it doubles as your 2-week check-in",
  },
} as const;

export type Pricing = typeof FACIAL_BALANCING_PRICING;
export type HaArea = keyof Pricing["perSyringe"];
export const HA_AREAS = Object.keys(FACIAL_BALANCING_PRICING.perSyringe) as HaArea[];

export type PlanSelection = {
  syringes: Partial<Record<HaArea, number>>;
  biostimulatorVials?: number;
};

/** A plan rule turns the à la carte unit prices of every selected HA syringe into a plan total. */
export type PlanRule = (unitPrices: number[], pricing: Pricing) => number;

export const PLAN_RULES: Record<string, PlanRule> = {
  /**
   * First syringe at its area price, each additional at the flat plan price, capped at
   * à la carte. "First" is the lowest-priced syringe in the plan, so a lip + cheek plan is
   * lip price + one flat syringe, and two lip syringes never cost more than two at menu.
   */
  "first-then-flat": (unitPrices, pricing) => {
    if (unitPrices.length === 0) return 0;
    const alaCarte = unitPrices.reduce((sum, price) => sum + price, 0);
    const first = Math.min(...unitPrices);
    const plan = first + (unitPrices.length - 1) * pricing.plan.additionalSyringe;
    return Math.min(plan, alaCarte);
  },
  /** No plan discount at all (useful if the owner pulls the plan rule). */
  "a-la-carte": unitPrices => unitPrices.reduce((sum, price) => sum + price, 0),
};

export type PlanEstimate = {
  lines: { area: HaArea; count: number; unitPrice: number; subtotal: number }[];
  haSyringes: number;
  haAlaCarte: number;
  haPlan: number;
  biostimulatorVials: number;
  biostimulatorTotal: number;
  alaCarteTotal: number;
  total: number;
  savings: number;
  /** True when the HA syringe count reaches the gift threshold. */
  giftUnlocked: boolean;
  /** HA syringes still needed for the gift (0 once unlocked). */
  syringesToGift: number;
};

const clampCount = (n: number | undefined) => Math.max(0, Math.floor(Number(n) || 0));

export function estimatePlan(selection: PlanSelection, pricing: Pricing = FACIAL_BALANCING_PRICING): PlanEstimate {
  const lines = HA_AREAS.map(area => {
    const count = clampCount(selection.syringes[area]);
    const unitPrice = pricing.perSyringe[area];
    return { area, count, unitPrice, subtotal: count * unitPrice };
  }).filter(line => line.count > 0);

  const unitPrices = lines.flatMap(line => Array.from({ length: line.count }, () => line.unitPrice));
  const rule = PLAN_RULES[pricing.plan.rule] ?? PLAN_RULES["a-la-carte"];
  const haAlaCarte = unitPrices.reduce((sum, price) => sum + price, 0);
  const haPlan = rule(unitPrices, pricing);

  const biostimulatorVials = clampCount(selection.biostimulatorVials);
  const biostimulatorTotal = biostimulatorVials * pricing.biostimulatorPerVial;

  const alaCarteTotal = haAlaCarte + biostimulatorTotal;
  const total = haPlan + biostimulatorTotal;
  return {
    lines,
    haSyringes: unitPrices.length,
    haAlaCarte,
    haPlan,
    biostimulatorVials,
    biostimulatorTotal,
    alaCarteTotal,
    total,
    savings: alaCarteTotal - total,
    giftUnlocked: unitPrices.length >= pricing.gift.minHaSyringes,
    syringesToGift: Math.max(0, pricing.gift.minHaSyringes - unitPrices.length),
  };
}

export const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
